import { useEffect, useState, useCallback } from 'react';
import { Box, Group, Stack, Text, Title, Badge, Loader, ActionIcon } from '@mantine/core';
import { IconShoppingBag, IconRefresh } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { fetchStaffTakeoutRequests, type TakeoutRequestView } from '../api-client.ts';
import { tokens } from '../ui/tokens.ts';

const STATUS_COLOR: Record<TakeoutRequestView['status'], string> = {
  submitted: 'blue',
  ready: 'green',
  completed: 'gray',
  cancelled: 'red',
};

const STATUS_LABEL: Record<TakeoutRequestView['status'], string> = {
  submitted: '已送單',
  ready: '可取餐',
  completed: '已完成',
  cancelled: '已取消',
};

export function TakeoutPanel({
  venueId,
  timezone,
}: {
  venueId: string;
  timezone: string;
}) {
  const [list, setList] = useState<TakeoutRequestView[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(() => {
    setLoading(true);
    fetchStaffTakeoutRequests(venueId)
      .then(setList)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [venueId]);

  useEffect(() => {
    reload();
  }, [reload]);

  return (
    <Box
      style={{
        padding: 20,
        background: tokens.color.cardBackground,
        borderRadius: tokens.radius.lg,
        border: `1px solid ${tokens.color.cardBorder}`,
      }}
    >
      <Group justify="space-between" mb="sm">
        <Group gap="xs">
          <IconShoppingBag size={18} color={tokens.color.primary} />
          <Title order={5}>外帶訂單</Title>
        </Group>
        <ActionIcon
          variant="subtle"
          onClick={reload}
          disabled={loading}
          size="sm"
          radius="xl"
        >
          <IconRefresh size={14} />
        </ActionIcon>
      </Group>

      {error && (
        <Text c="red" size="sm">
          {error}
        </Text>
      )}

      {loading && list.length === 0 && (
        <Group justify="center" py="sm">
          <Loader size="sm" />
        </Group>
      )}

      {!loading && list.length === 0 && !error && (
        <Text c="dimmed" size="sm">
          目前沒有外帶訂單
        </Text>
      )}

      <Stack gap="xs">
        {list.map((t) => (
          <Box
            key={t.requestId}
            style={{
              padding: 12,
              borderRadius: tokens.radius.md,
              border: `1px solid ${tokens.color.cardBorder}`,
            }}
          >
            <Group justify="space-between" wrap="nowrap">
              <Stack gap={2}>
                <Text fw={600} size="sm">
                  {t.customerRef}
                </Text>
                <Text size="xs" c="dimmed">
                  取餐 {dayjs(t.pickupTimeIso).tz(timezone).format('MM/DD HH:mm')}
                </Text>
              </Stack>
              <Badge color={STATUS_COLOR[t.status]} variant="light">
                {STATUS_LABEL[t.status]}
              </Badge>
            </Group>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
